import React, { useState, useMemo, useEffect } from "react";
import { Box, Container } from "@mui/material";
import BackgroundImageCst from "@components/BackgroundImageCst";
import HeroCst from "@components/HeroCst";
import { useFetchDetail, useFetchDataDetail } from "@hooks/useFetchDetail";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "@components/Breadcrumb";
import { useTranslation } from "react-i18next";
import { ServicesURL, getAllErrorsAsString } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import AnimationFadeIn from "@components/AnimationFadeIn";
import { MethodType } from "src/services/type";
import useSWRMutation from "swr/mutation";
import { fetcher } from "src/services/config";
import ModalCst, { OutcomesInterface } from "@components/ModalCst";
import { useAddEditDetailCost } from "../hooks/useAddEditDetailCost";
import DetailCostFormCst from "../DetailCostFormCst";
import dayjs from "dayjs";

const EditDetailCost: React.FC<{
  accounting?: boolean;
}> = ({ accounting = false }) => {
  const { tab, idProject, id, idCostModel, idCostModelDetail } = useParams();
  const { t } = useTranslation(["projects-cost-detail"]);
  const navigate = useNavigate();
  const [openOutcome, setOpenOutcome] = useState(false);

  const {
    columns,
    dataDetail: dataCostDetail,
    mutate,
  } = useFetchDataDetail(
    `${ServicesURL.getCostModelDetail}/${idCostModelDetail}?corporateId=${id}`,
    PagesID["projects.cost-detail"],
  );

  /*Get the project information*/
  const { dataDetail: dataProject, mutate: projectMutate } = useFetchDataDetail(
    `${ServicesURL.getProjects}/${idProject}?corporateId=${id}`,
    PagesID["projects.cost-model"],
  );

  /*Get the cost model information*/
  const { dataDetail: dataCostModel, mutate: costModelMutate } =
    useFetchDataDetail(
      `${ServicesURL.postCostModel.replace(":budgetProjectId", String(idProject))}/${idCostModel}?corporateId=${id}`,
      PagesID["projects.cost-model"],
    );

  const {
    handleAssociation,
    setOpenAssociation,
    handleDelete,
    getValues,
    resetContractAssociations,
    setSupplierAssociation,
    setContractAssociation,
    contractAssociation,
    supplierAssociation,
    openAssociations,
  } = useAddEditDetailCost([], dataCostModel);

  const getInitialOutcome = () => {
    return {
      budget: {
        code: undefined,
        message: t("edit.outcome.cost-detail.messageLoading"),
        title: t("edit.outcome.cost-detail.title"),
      },
    };
  };

  const initialOutcome: OutcomesInterface = {
    budget: {
      code: undefined,
      message: t("edit.outcome.cost-detail.messageLoading"),
      title: t("edit.outcome.cost-detail.title"),
    },
  };

  const [outcome, setOutcome] = useState<OutcomesInterface>(initialOutcome);

  // CUSTOM API FOR APPLICATION/JSON-PATCH+JSON
  const { trigger: triggerCostDetail } = useSWRMutation(
    `${ServicesURL.postCostModelDetail}/${idCostModelDetail}?corporateId=${id}`,
    (url: string, arg: { arg: any }) =>
      fetcher<any>(`${url}`, MethodType.PUT, { ...arg.arg }),
  );

  /*Submit of the form for create a new cost model*/
  const onSubmit = async (inputForm: any) => {
    setOpenOutcome(true);
    const initialOutcomeState: any = getInitialOutcome();
    setOutcome(initialOutcomeState);
    const newOutcome: any = JSON.parse(JSON.stringify(initialOutcomeState));

    const objToSend: any = {
      title: inputForm.title || null,
      cost_model_item_id: idCostModel ? parseInt(idCostModel, 10) : null,
      status: inputForm.status || null,
      management_type: inputForm.management_type || null,
      qt: parseInt(inputForm.qt, 10) || 0,
      unit_cost: parseFloat(inputForm.unit_cost) || 0,
      supplier_id:
        supplierAssociation && supplierAssociation.length > 0
          ? parseInt(supplierAssociation[0].id)
          : null,
      contract_id:
        contractAssociation && contractAssociation.length > 0
          ? parseInt(contractAssociation[0].id)
          : null,
      notes: inputForm.notes || null,
      insert_date: !!inputForm.insert_date
        ? dayjs(new Date(inputForm.insert_date)).format("YYYY-MM-DD")
        : null,
      rda_sap_code: inputForm.rda_sap_code || null,
      rda_value: inputForm.rda_value || null,
      fc_release_date: !!inputForm.fc_release_date
        ? dayjs(new Date(inputForm.fc_release_date)).format("YYYY-MM-DD")
        : null,
      oda_sap_code: inputForm.oda_sap_code || null,
      oda_sap_value: inputForm.oda_sap_value || null,
      oda_sap_date: !!inputForm.oda_sap_date
        ? dayjs(new Date(inputForm.oda_sap_date)).format("YYYY-MM-DD")
        : null,
    };

    try {
      await triggerCostDetail(objToSend);
      newOutcome.budget.code = "OK";
      newOutcome.budget.message = t("edit.outcome.cost-detail.messageOK");
      setOutcome(newOutcome);
    } catch (error) {
      newOutcome.budget.code = "KO";
      newOutcome.budget.message = getAllErrorsAsString(error);
      setOutcome(newOutcome);
    }
    mutate(inputForm);
    projectMutate(inputForm);
    costModelMutate(inputForm);
  };

  /*Building the data to send at the form with project and costmodel info*/
  const formField = useMemo(() => {
    if (
      dataCostModel.dto != null &&
      dataProject.dto != null &&
      dataCostDetail &&
      Object.keys(dataCostDetail).length > 0
    ) {
      return {
        ...dataCostDetail,
        info_project: dataProject?.dto || null,
        info_cost_model: dataCostModel?.dto || null,
      };
    } else {
      return null;
    }
  }, [dataCostModel, dataProject, dataCostDetail]);

  return (
    <AnimationFadeIn>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-cost-centers.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst title={t("edit.hero-title")} description={""} />
        {columns && (
          <Box
            sx={{
              mt: "25px",
            }}
          >
            <DetailCostFormCst
              row={formField}
              columns={columns}
              onSubmit={onSubmit}
              backButton={() => {
                accounting
                  ? navigate(
                      `/corporate/${id}/detail/budget-management/accounting?tab=${tab || ""}`,
                    )
                  : navigate(-1);
              }}
              setOpenAssociation={(accessorKey, open) =>
                setOpenAssociation(accessorKey, open)
              }
              handleAssociation={(accessorKey, value) =>
                handleAssociation(accessorKey, value)
              }
              handleDelete={(accessorKey, value) =>
                handleDelete(accessorKey, value)
              }
              getValues={(accessorKey) => getValues(accessorKey)}
              resetContractAssociations={resetContractAssociations}
              association={openAssociations}
              fullWidth={true}
              supplierAssociation={supplierAssociation}
              setContractAssociation={setContractAssociation}
              setSupplierAssociation={setSupplierAssociation}
              isNew={false}
              isAccounting={accounting}
            />
          </Box>
        )}
      </Container>
      <ModalCst
        open={openOutcome}
        setOpen={setOpenOutcome}
        typeOfModal={"outcome"}
        data={{
          outcome: outcome,
          callBackSubmit: () => {
            setOpenOutcome(!openOutcome);
            navigate(
              accounting
                ? `/corporate/${id}/detail/budget-management/accounting?tab=${tab || ""}`
                : `/corporate/${id}/detail/budget-management/projects/edit/${idProject}/cost-model/${idCostModel}/cost`,
            );
          },
        }}
      />
    </AnimationFadeIn>
  );
};

export default EditDetailCost;
